import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "../ui/card";
import QuestionRenderer from "./questions/QuestionRenderer";
import SurveyNav from "./SurveyNav";
import SurveyProgress from "./SurveyProgress";
import { useSurveyState } from "./useSurveyState";
import { scrollSurveyToTop } from "./scrollSurvey";
import {
  firstBlockingInSurvey,
  firstBlockingOnPage,
  isAnswered,
  isOtherIncomplete,
  pageIndexForQuestionId,
  progressCounts,
  questionFormatError,
} from "./surveyUtils";

const TEXT_DEBOUNCE_MS = 400;

/**
 * @param {string} questionId
 * @returns {void}
 */
function scrollToQuestion(questionId) {
  const node = document.getElementById(`survey-block-${questionId}`);
  node?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/**
 * Paged survey engine with autosave.
 *
 * @param {Object} props
 * @param {object} props.definition
 * @param {Record<string, unknown>} [props.initialAnswers]
 * @param {string} [props.initialPageId]
 * @param {"draft"|"edit"|"readonly"} [props.mode]
 * @param {function(Record<string, unknown>, string): Promise<unknown>} [props.onSave]
 * @param {function(Record<string, unknown>, string): Promise<unknown>} [props.onSubmit]
 * @param {() => void} [props.onDone]
 * @param {string} [props.submitLabel]
 * @param {boolean} [props.embedded]
 * @param {function(Record<string, unknown>): void} [props.onAnswersChange]
 * @returns {JSX.Element}
 */
export default function SurveyRunner({
  definition,
  initialAnswers = {},
  initialPageId = "",
  mode = "draft",
  onSave,
  onSubmit,
  onDone,
  submitLabel,
  embedded = false,
  onAnswersChange,
}) {
  const readOnly = mode === "readonly";
  const settings = definition?.settings || {};
  const allowBack = settings.allowBack !== false;
  const {
    answers,
    setAnswer,
    pageIndex,
    setPageIndex,
    page,
    pages,
    visibleOnPage,
    currentPageId,
  } = useSurveyState(definition, initialAnswers, initialPageId);
  const [submitting, setSubmitting] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showValidation, setShowValidation] = useState(false);
  const timerRef = useRef(null);
  const latestRef = useRef({ answers, pageId: currentPageId });
  const submittedRef = useRef(false);
  const pendingScrollId = useRef(null);
  const prevPageIndexRef = useRef(pageIndex);
  const uploadingIdsRef = useRef(new Set());

  latestRef.current = { answers, pageId: currentPageId };

  useEffect(() => {
    const pageChanged = prevPageIndexRef.current !== pageIndex;
    prevPageIndexRef.current = pageIndex;
    if (pendingScrollId.current) {
      const questionId = pendingScrollId.current;
      pendingScrollId.current = null;
      const frame = window.requestAnimationFrame(() => scrollToQuestion(questionId));
      return () => window.cancelAnimationFrame(frame);
    }
    if (!pageChanged) return undefined;
    return scrollSurveyToTop();
  }, [pageIndex]);

  /**
   * Persist answers immediately (clears a pending debounce).
   * @param {Record<string, unknown>} nextAnswers
   * @param {string} pageId
   * @returns {Promise<void>}
   */
  async function persistNow(nextAnswers, pageId) {
    if (readOnly || !onSave || submittedRef.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setSaveError("");
    const result = await onSave(nextAnswers, pageId);
    if (result && result.error) {
      setSaveError(result.error);
    }
  }

  /**
   * Persist now or after a short debounce for text fields.
   * @param {Record<string, unknown>} nextAnswers
   * @param {string} pageId
   * @param {boolean} immediate
   * @returns {void}
   */
  function scheduleSave(nextAnswers, pageId, immediate) {
    if (readOnly || !onSave || submittedRef.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (immediate) {
      persistNow(nextAnswers, pageId);
      return;
    }
    timerRef.current = setTimeout(() => {
      persistNow(nextAnswers, pageId);
    }, TEXT_DEBOUNCE_MS);
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!readOnly && onSave && !submittedRef.current) {
        const latest = latestRef.current;
        onSave(latest.answers, latest.pageId);
      }
    };
    // Flush pending text on unmount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = progressCounts(definition, answers, pageIndex);

  /**
   * @param {unknown} value
   * @param {{ immediate: boolean }} meta
   * @param {string} questionId
   * @returns {void}
   */
  function handleChange(questionId, value, meta) {
    const next = setAnswer(questionId, value);
    onAnswersChange?.(next);
    if (meta?.uploading) {
      uploadingIdsRef.current.add(questionId);
    } else {
      uploadingIdsRef.current.delete(questionId);
    }
    setUploadBusy(uploadingIdsRef.current.size > 0);
    scheduleSave(next, currentPageId, Boolean(meta?.immediate));
  }

  /**
   * @param {number} nextIndex
   * @returns {void}
   */
  function goToPage(nextIndex) {
    const nextId = pages[nextIndex]?.id || currentPageId;
    persistNow(answers, nextId);
    setPageIndex(nextIndex);
  }

  /**
   * @returns {void}
   */
  function handleNext() {
    if (uploadBusy) return;
    const blocker = firstBlockingOnPage(page, answers);
    if (blocker) {
      setShowValidation(true);
      scrollToQuestion(blocker.id);
      return;
    }
    setShowValidation(false);
    goToPage(pageIndex + 1);
  }

  /**
   * @returns {Promise<void>}
   */
  async function handleSubmit() {
    if (uploadBusy) return;
    const blocker = firstBlockingInSurvey(definition, answers);
    if (blocker) {
      setShowValidation(true);
      const blockerPage = pageIndexForQuestionId(definition, blocker.id);
      if (blockerPage >= 0 && blockerPage !== pageIndex) {
        pendingScrollId.current = blocker.id;
        goToPage(blockerPage);
        return;
      }
      scrollToQuestion(blocker.id);
      return;
    }
    if (!onSubmit) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setSubmitting(true);
    setSaveError("");
    const result = await onSubmit(answers, currentPageId);
    setSubmitting(false);
    if (result && result.error) {
      setSaveError(result.error);
      return;
    }
    submittedRef.current = true;
    onDone?.();
  }

  const body = (
    <>
        {page?.title ? (
          <h2 className="mb-1 text-xl font-semibold text-text-primary">{page.title}</h2>
        ) : null}
        {page?.description ? (
          <p className="mb-6 text-sm text-text-secondary">{page.description}</p>
        ) : (
          <div className="mb-6" />
        )}
        <div className="flex flex-col gap-8">
          {visibleOnPage.map((question) => {
            const value = answers[question.id];
            const formatError = questionFormatError(question, value);
            const otherError = isOtherIncomplete(question, value) ? "Please specify." : "";
            const error = showValidation ? formatError || otherError : "";
            const showRequired =
              showValidation && Boolean(question.required) && !isAnswered(question, value);
            return (
              <div
                key={question.id}
                id={`survey-block-${question.id}`}
                className="scroll-mt-24"
              >
                <QuestionRenderer
                  question={question}
                  value={value}
                  readOnly={readOnly}
                  error={error}
                  showRequired={showRequired}
                  onChange={(nextValue, meta) => handleChange(question.id, nextValue, meta)}
                />
              </div>
            );
          })}
        </div>
        {saveError ? (
          <p className="mt-4 text-sm text-error" role="alert">
            {saveError}
          </p>
        ) : null}
        <SurveyNav
          canBack={pageIndex > 0}
          isLast={pageIndex >= pages.length - 1}
          allowBack={allowBack}
          readOnly={readOnly}
          mode={mode}
          saving={submitting || uploadBusy}
          pageIndex={pageIndex}
          pageCount={pages.length}
          onBack={() => goToPage(pageIndex - 1)}
          onNext={handleNext}
          onSubmit={handleSubmit}
          onDone={onDone}
          submitLabel={submitLabel}
        />
    </>
  );

  if (embedded) {
    return <div id="survey-scroll-top">{body}</div>;
  }

  return (
    <Card id="survey-scroll-top" className="overflow-hidden border-ui-border">
      <SurveyProgress answered={counts.answered} total={counts.total} />
      <CardContent className="p-6 sm:p-8">{body}</CardContent>
    </Card>
  );
}
